import { attributes } from '../../core/ids-attributes';
import { IdsBaseConstructor } from '../../core/ids-element';
import { stringToBool } from '../../utils/ids-string-utils/ids-string-utils';

/** Tag names of preceding elements whose height should be subtracted. */
const OFFSET_SIBLINGS = ['ids-header', 'ids-theme-switcher'];

type Constraints = IdsBaseConstructor;

/**
 * Adds a `fit-viewport` attribute to a layout container. When enabled the host
 * gets an explicit height of `calc(100% - previousSiblingHeight)` so it fills
 * the remaining viewport space below an adjacent element like ids-header,
 * mirroring ids-splitter's height-adjustment approach.
 *
 * Shared by ids-layout-grid and ids-layout-flex.
 * @param {any} superclass Accepts a superclass and creates a new subclass from it
 * @returns {any} The extended object
 */
const IdsFitViewportMixin = <T extends Constraints>(superclass: T) => class extends superclass {
  constructor(...args: any[]) {
    super(...args);
  }

  static get attributes() {
    return [
      ...(superclass as any).attributes,
      attributes.FIT_VIEWPORT
    ];
  }

  /**
   * Observes this host, its parent, and its previous sibling for size changes
   * to keep the fit-viewport height accurate. A single observer instance can
   * watch multiple elements.
   * @private
   */
  #fitViewportResizeObserver = new ResizeObserver(() => this.#fitViewportResize());

  /**
   * One-shot observer that gates the first fit-viewport measurement until the
   * host is actually visible/laid out (see #applyFitViewport).
   * @private
   */
  #fitViewportInitObserver?: IntersectionObserver;

  /**
   * Watches the previous sibling for display:none/hidden toggles, which
   * ResizeObserver does not report.
   * @private
   */
  #fitViewportMutationObserver?: MutationObserver;

  /** Debounce flag so the height adjustment runs once per animation frame. */
  #fitViewportResizePending = false;

  connectedCallback() {
    super.connectedCallback?.();
    if (this.fitViewport) this.#applyFitViewport();
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.#teardownFitViewport();
  }

  /**
   * Set the fit-viewport attribute. When enabled, sets an explicit height of
   * `calc(100% - previousSiblingHeight)` so the host fills the remaining
   * viewport space below an adjacent element like ids-header.
   * @param {boolean | string} value true to enable viewport fitting
   */
  set fitViewport(value: boolean | string) {
    const isTruthy = stringToBool(value);
    if (isTruthy) {
      this.setAttribute(attributes.FIT_VIEWPORT, '');
      this.#applyFitViewport();
    } else {
      this.removeAttribute(attributes.FIT_VIEWPORT);
      this.#teardownFitViewport();
      this.style.removeProperty('height');
    }
  }

  /**
   * Get the fit-viewport attribute
   * @returns {boolean} true if viewport fitting is enabled
   */
  get fitViewport(): boolean {
    return stringToBool(this.getAttribute(attributes.FIT_VIEWPORT));
  }

  /**
   * Adjust this host's height to account for an adjacent preceding element
   * (e.g. ids-header or ids-theme-switcher), mirroring ids-splitter's
   * height-adjustment approach.
   * @private
   */
  #adjustHeightForPreviousSibling(): void {
    const prev = this.previousElementSibling;
    requestAnimationFrame(() => {
      if (prev && OFFSET_SIBLINGS.includes(prev.tagName.toLowerCase())) {
        const height = prev.getBoundingClientRect().height;
        if (height > 0) {
          this.style.setProperty('height', `calc(100% - ${height}px)`);
        } else {
          this.style.setProperty('height', '100%');
        }
      } else {
        this.style.setProperty('height', '100%');
      }
    });
  }

  /**
   * Re-run the height adjustment, debounced to one call per animation frame.
   * @private
   */
  #fitViewportResize(): void {
    if (this.#fitViewportResizePending) return;
    this.#fitViewportResizePending = true;
    requestAnimationFrame(() => {
      this.#fitViewportResizePending = false;
      this.#adjustHeightForPreviousSibling();
    });
  }

  /**
   * Apply fit-viewport height adjustment.
   *
   * getBoundingClientRect() reads 0 until the host (and its previous sibling)
   * have actually been laid out and become visible, so mirror ids-splitter's
   * #initObserver: a one-shot IntersectionObserver gates the first measurement.
   * After that, a ResizeObserver on this host, its parent, and its previous
   * sibling (e.g. ids-header) — plus a MutationObserver watching for
   * display:none/hidden toggles on the sibling, which ResizeObserver misses —
   * keep the height accurate as content around it changes.
   * @private
   */
  #applyFitViewport(): void {
    this.#fitViewportResizeObserver.disconnect();
    this.#fitViewportResizeObserver.observe(this);
    if (this.parentElement) this.#fitViewportResizeObserver.observe(this.parentElement);

    const prevSibling = this.previousElementSibling;
    this.#fitViewportMutationObserver?.disconnect();
    if (prevSibling) {
      this.#fitViewportResizeObserver.observe(prevSibling);
      this.#fitViewportMutationObserver = new MutationObserver(() => this.#fitViewportResize());
      this.#fitViewportMutationObserver.observe(prevSibling, { attributes: true, attributeFilter: ['style', 'hidden'] });
    }

    if ('IntersectionObserver' in window) {
      this.#fitViewportInitObserver?.disconnect();
      this.#fitViewportInitObserver = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          this.#fitViewportInitObserver?.disconnect();
          this.#adjustHeightForPreviousSibling();
        }
      });
      this.#fitViewportInitObserver.observe(this);
    } else {
      this.#adjustHeightForPreviousSibling();
    }
  }

  /**
   * Disconnect all fit-viewport observers.
   * @private
   */
  #teardownFitViewport(): void {
    this.#fitViewportResizeObserver.disconnect();
    this.#fitViewportInitObserver?.disconnect();
    this.#fitViewportMutationObserver?.disconnect();
  }
};

export default IdsFitViewportMixin;
